/**
 * Modal de création / édition d'un événement de l'agenda staff (Sauf Imprévu).
 * Cloné strict sur EditProductSheet : overlay full-screen, masque la TabBar,
 * pipeline cover identique (canvas downscale 1600px Q0.85 → POST /cover).
 *
 * Mode : `event === null` → création (POST /events), sinon édition (PUT /events/:id).
 * Pour la création, la cover est uploadée après le POST (besoin de l'id). En
 * édition, le POST /cover se fait d'abord (comme products-enriched), puis le PUT.
 */
import React, { useEffect, useRef, useState } from 'react';
import { EVENT_TYPES, type AgendaEvent, type EventType } from './eventTypes';
import './EventEditSheet.css';
import { apiFetch } from '../../utils/api';

const TITRE_MAX = 200;
const DESCRIPTION_MAX = 2000;

interface Props {
  /** null = mode création. */
  event: AgendaEvent | null;
  /** Pré-remplit date_debut en mode création (jour sélectionné dans l'agenda). */
  defaultDate?: string;
  onClose: () => void;
  onSaved: (saved: AgendaEvent) => void;
  onDeleted?: (id: string) => void;
}

// Downscale + ré-encode JPEG avant base64 (anti-OOM WKWebView iPhone). Le backend
// recompresse ensuite en 600×400 WebP. Voie principale : createImageBitmap avec
// resizeWidth/Height → décode DIRECTEMENT à la taille réduite, sans jamais tenir
// le bitmap plein format en mémoire (une photo 48 MP = ~195 MB décodés = crash
// WKWebView au retour du picker). Fallback `new Image()` pour très vieux iOS.
async function fileToDataUrl(file: File, maxEdge = 1600, quality = 0.85): Promise<string> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    // Re-décode directement à la taille cible (vraie protection OOM).
    const resized = await createImageBitmap(bitmap, {
      resizeWidth: w,
      resizeHeight: h,
      resizeQuality: 'high',
    });
    bitmap.close(); // libère le bitmap original
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');
    ctx.drawImage(resized, 0, 0);
    resized.close();
    return canvas.toDataURL('image/jpeg', quality);
  } catch {
    // Fallback (très anciens iOS sans createImageBitmap) : ancienne méthode.
    const objectUrl = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Image load failed'));
        el.src = objectUrl;
      });
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D context unavailable');
      ctx.drawImage(img, 0, 0, w, h);
      return canvas.toDataURL('image/jpeg', quality);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

/**
 * Euros saisis → centimes. Accepte la virgule décimale (clavier FR). Champ vide
 * ou illisible → null : « prix non renseigné », que la page publique affiche
 * « Entrée libre ». Un 0 explicite reste un 0 (gratuit annoncé).
 */
function parsePriceCents(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (!trimmed) return null;
  const euros = Number(trimmed);
  if (!Number.isFinite(euros) || euros < 0) return null;
  return Math.round(euros * 100);
}

/** Places saisies → entier positif, ou null si vide/illisible. */
function parseCapacity(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number.parseInt(trimmed, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function EventEditSheet({ event, defaultDate, onClose, onSaved, onDeleted }: Props) {
  const isEdit = event !== null;
  const [titre, setTitre] = useState('');
  const [type, setType] = useState<EventType>('degustation');
  const [description, setDescription] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [heureDebut, setHeureDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [heureFin, setHeureFin] = useState('');
  // Le backend stocke le prix en CENTIMES ; le staff saisit des EUROS. La
  // conversion se fait au save (et à l'hydratation), jamais dans l'état.
  const [priceEuros, setPriceEuros] = useState('');
  const [capacity, setCapacity] = useState('');
  const [published, setPublished] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingCoverDataUrl, setPendingCoverDataUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const titreInputRef = useRef<HTMLInputElement | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Hydrate l'état au changement d'événement (ou ouverture en création).
  useEffect(() => {
    setTitre(event?.titre ?? '');
    setType(event?.type ?? 'degustation');
    setDescription(event?.description ?? '');
    setDateDebut(event?.dateDebut ?? defaultDate ?? '');
    setHeureDebut(event?.heureDebut ?? '');
    setDateFin(event?.dateFin ?? '');
    setHeureFin(event?.heureFin ?? '');
    setPriceEuros(event?.price != null ? String(event.price / 100) : '');
    setCapacity(event?.capacity != null ? String(event.capacity) : '');
    // Création : publié par défaut. Le staff crée un événement pour l'annoncer ;
    // lui demander une action de plus pour qu'il soit visible ferait des
    // « événements fantômes » invisibles côté client sans que personne comprenne.
    setPublished(event ? event.published : true);
    setPreviewUrl(event?.coverImageUrl ?? null);
    setPendingCoverDataUrl(null);
    setToast(null);
  }, [event?.id, defaultDate]);

  // Masque la TabBar tant que la modale est ouverte (cf TabBar.css / modal-sheet-open).
  useEffect(() => {
    document.body.classList.add('modal-sheet-open');
    return () => document.body.classList.remove('modal-sheet-open');
  }, []);

  // Auto-focus le titre à l'ouverture en mode création (focus immédiat sur
  // l'action). En édition, on ne vole pas le focus (le titre est déjà rempli).
  useEffect(() => {
    if (!isEdit) {
      const t = setTimeout(() => titreInputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [isEdit]);

  const handleFile = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      setToast({ kind: 'err', msg: 'Image trop lourde (>10 MB)' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      setToast({ kind: 'err', msg: 'Format invalide (image attendue)' });
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setPendingCoverDataUrl(dataUrl);
      setPreviewUrl(dataUrl);
    } catch {
      setToast({ kind: 'err', msg: 'Lecture du fichier échouée' });
    }
  };

  const onPickCover = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await handleFile(file);
  };

  const onDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      setIsDraggingOver(true);
    }
  };
  const onDragLeave = () => setIsDraggingOver(false);
  const onDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  // Titre + date obligatoires : le bouton Enregistrer est désactivé tant qu'ils
  // sont vides (cf. `canSave`), donc plus de toast "Titre requis" à la volée.
  const canSave = titre.trim().length > 0 && dateDebut.length > 0;

  const onSave = async () => {
    if (saving || !canSave) return;
    setSaving(true);
    setToast(null);
    const token = localStorage.getItem('alterrego_jwt');
    if (!token) {
      setToast({ kind: 'err', msg: 'Non connecté' });
      setSaving(false);
      return;
    }
    const headers = { 'Content-Type': 'application/json' };
    // Payload commun (création + update). date_fin par défaut = date_debut.
    const payload = {
      titre: titre.trim(),
      type,
      description: description.trim() || null,
      dateDebut,
      heureDebut: heureDebut || null,
      dateFin: dateFin || dateDebut,
      heureFin: heureFin || null,
      // Champ vide → null (« pas d'info »), pas 0 (« gratuit ») ni 0 place.
      price: parsePriceCents(priceEuros),
      capacity: parseCapacity(capacity),
      published,
    };
    try {
      let saved: AgendaEvent;
      if (isEdit && event) {
        // Édition : cover d'abord (si nouvelle), puis PUT champs.
        if (pendingCoverDataUrl) {
          const rc = await apiFetch(`/events/${event.id}/cover`, {
            method: 'POST', headers, body: JSON.stringify({ dataUrl: pendingCoverDataUrl }),
          });
          if (!rc.ok) { setToast({ kind: 'err', msg: 'Visuel non enregistré' }); setSaving(false); return; }
        }
        const r = await apiFetch(`/events/${event.id}`, {
          method: 'PUT', headers, body: JSON.stringify(payload),
        });
        if (!r.ok) { setToast({ kind: 'err', msg: 'Enregistrement échoué' }); setSaving(false); return; }
        saved = await r.json();
      } else {
        // Création : POST puis cover (besoin de l'id retourné).
        const r = await apiFetch(`/events`, {
          method: 'POST', headers, body: JSON.stringify(payload),
        });
        if (!r.ok) { setToast({ kind: 'err', msg: 'Création échouée' }); setSaving(false); return; }
        saved = await r.json();
        if (pendingCoverDataUrl) {
          const rc = await apiFetch(`/events/${saved.id}/cover`, {
            method: 'POST', headers, body: JSON.stringify({ dataUrl: pendingCoverDataUrl }),
          });
          if (!rc.ok) {
            // L'event EST créé côté serveur (pas de rollback). On garde le sheet
            // OUVERT et on n'affiche PAS "Enregistré" : onSaved rafraîchit la liste
            // (l'event existe), l'utilisateur peut re-tenter le visuel.
            onSaved(saved);
            setToast({ kind: 'err', msg: 'Événement créé, mais visuel non enregistré — réessaie le visuel' });
            setSaving(false);
            return;
          }
          const data = await rc.json();
          if (data?.event) saved = data.event as AgendaEvent;
        }
      }
      setToast({ kind: 'ok', msg: 'Enregistré' });
      onSaved(saved);
      setTimeout(() => onClose(), 600);
    } catch {
      setToast({ kind: 'err', msg: 'Erreur réseau — réessaie' });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit || !event || deleting) return;
    if (!window.confirm(`Supprimer l'événement « ${event.titre} » ?`)) return;
    setDeleting(true);
    setToast(null);
    const token = localStorage.getItem('alterrego_jwt');
    if (!token) { setToast({ kind: 'err', msg: 'Non connecté' }); setDeleting(false); return; }
    try {
      const r = await apiFetch(`/events/${event.id}`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error(`Delete ${r.status}`);
      onDeleted?.(event.id);
      onClose();
    } catch (err: any) {
      setToast({ kind: 'err', msg: err?.message ?? 'Suppression échouée' });
      setDeleting(false);
    }
  };

  const busy = saving || deleting;

  return (
    <div className="ees-backdrop" onClick={onClose}>
      <div className="ees-sheet" onClick={(e) => e.stopPropagation()}>
        <button className="ees-close" onClick={onClose} aria-label="Fermer">×</button>

        <div className="ees-header">
          <h2>{isEdit ? "Éditer l'événement" : 'Nouvel événement'}</h2>
          {isEdit && <p className="ees-event-name">{event?.titre}</p>}
        </div>

        {/* Cover — tap zone (caméra/photothèque/fichier) + drag&drop desktop */}
        <div className="ees-section">
          <h3>Visuel</h3>
          <button
            type="button"
            className={`ees-cover-tap${previewUrl ? ' has-cover' : ''}${isDraggingOver ? ' dragging-over' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            aria-label={previewUrl ? 'Changer le visuel' : 'Ajouter un visuel'}
          >
            {previewUrl ? (
              <>
                <img src={previewUrl} alt="" />
                <span className="ees-cover-edit-badge" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                </span>
              </>
            ) : (
              <span className="ees-cover-empty-content">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <span>Touchez pour ajouter un visuel</span>
                <span className="ees-cover-empty-drop-hint">ou glissez un fichier</span>
              </span>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={onPickCover}
          />
          <p className="ees-hint">Paysage 600×400, &lt; 10 MB</p>
        </div>

        {/* Titre */}
        <div className="ees-section">
          <h3>Titre <span className="ees-req" aria-hidden="true">*</span></h3>
          <input
            ref={titreInputRef}
            className="ees-input"
            value={titre}
            onChange={(e) => setTitre(e.target.value.slice(0, TITRE_MAX))}
            placeholder="Soirée accords mets & vins…"
          />
        </div>

        {/* Type */}
        <div className="ees-section">
          <h3>Type</h3>
          <div className="ees-chips">
            {EVENT_TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`ees-chip${type === t.key ? ' active' : ''}`}
                style={type === t.key ? { background: t.color, borderColor: t.color, color: '#fff' } : undefined}
                onClick={() => setType(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dates / horaires */}
        <div className="ees-section">
          <h3>Créneau</h3>
          <div className="ees-datetime-grid">
            <label className="ees-field">
              <span>Début <span className="ees-req" aria-hidden="true">*</span></span>
              <input type="date" className="ees-input" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
            </label>
            <label className="ees-field">
              <span>Heure</span>
              <input type="time" className="ees-input" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)} />
            </label>
            <label className="ees-field">
              <span>Fin</span>
              <input type="date" className="ees-input" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
            </label>
            <label className="ees-field">
              <span>Heure</span>
              <input type="time" className="ees-input" value={heureFin} onChange={(e) => setHeureFin(e.target.value)} />
            </label>
          </div>
          <p className="ees-hint">Fin optionnelle (par défaut = jour de début). Une soirée peut finir au lendemain.</p>
        </div>

        {/* Infos pratiques — prix et places, AFFICHAGE SEUL côté client : aucun
            paiement, aucun décompte de places. Les deux champs sont facultatifs. */}
        <div className="ees-section">
          <h3>Infos pratiques</h3>
          <div className="ees-datetime-grid">
            <label className="ees-field">
              <span>Tarif (€)</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.5"
                className="ees-input"
                value={priceEuros}
                onChange={(e) => setPriceEuros(e.target.value)}
                placeholder="Entrée libre"
              />
            </label>
            <label className="ees-field">
              <span>Places</span>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                className="ees-input"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="Illimité"
              />
            </label>
          </div>
          <p className="ees-hint">Laisser vide = « Entrée libre » et aucune mention de places.</p>
        </div>

        {/* Publication — le seul interrupteur entre l'agenda staff et l'app
            cliente. Décoché = brouillon : l'événement reste ici, invisible des
            clients, de l'agenda public et du partage. */}
        <div className="ees-section">
          <h3>Publication</h3>
          <label className="ees-publish">
            <input
              type="checkbox"
              checked={published}
              onChange={(e) => setPublished(e.target.checked)}
            />
            <span>
              <strong>{isEdit ? 'Visible par les clients' : 'Publier immédiatement'}</strong>
              <em>
                {published
                  ? "Affiché sur l'accueil, l'agenda et partageable."
                  : 'Brouillon : visible du staff uniquement.'}
              </em>
            </span>
          </label>
        </div>

        {/* Description */}
        <div className="ees-section">
          <h3>Description</h3>
          <textarea
            className="ees-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
            placeholder="Détails, intervenant, tarif, réservation…"
            rows={4}
          />
          <p className="ees-counter">{description.length} / {DESCRIPTION_MAX}</p>
        </div>

        {/* Toast */}
        {toast && <div className={`ees-toast ${toast.kind}`}>{toast.msg}</div>}

        {/* Footer sticky : hint d'activation + boutons regroupés pour rester
            solidaires (sinon le sticky du footer passe par-dessus le hint). */}
        <div className="ees-footer">
          {!isEdit && !canSave && (
            <p className="ees-save-hint">Renseigne un titre pour activer la création</p>
          )}
          <div className="ees-actions">
            {isEdit && (
              <button type="button" className="ees-btn ees-btn-danger" onClick={onDelete} disabled={busy}>
                {deleting ? 'Suppression…' : 'Supprimer'}
              </button>
            )}
            <button type="button" className="ees-btn ees-btn-secondary" onClick={onClose} disabled={busy}>
              Annuler
            </button>
            <button type="button" className="ees-btn ees-btn-primary" onClick={onSave} disabled={busy || !canSave}>
              {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer' : "Créer l'événement"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
